import { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { SimpleReportCardProps } from 'components/organisms/SimpleReportCard';
import {
  getReportCategoriesListService,
  getReportListDataService,
} from 'services/analyticsCenter';
import {
  ReportCategoriesListTypes,
  ReportListDataTypes,
} from 'services/analyticsCenter/types';
import { useAppSelector } from 'store/hooks';
import { getStaticURL } from 'utils/language';

const useDownloadReport = (isHomePage?: boolean) => {
  const { t, ready } = useTranslation();
  const isChangeLanguage = useAppSelector(
    (state) => state.system.isChangeLanguage,
  );
  const { search } = useLocation();
  const codeUrl = new URLSearchParams(search).get('code');
  const { data: dataUser } = useAppSelector((state) => state.user);
  const [openLoginSSO, setOpenLoginSSO] = useState(false);
  const [loadingReportList, setLoadingReportList] = useState(false);
  const [reportCategoryData, setReportCategoryData] = useState<
    ReportCategoriesListTypes[]
  >([]);
  const [reportListData, setReportListData] = useState<
    SimpleReportCardProps[]
  >();
  const [totalPage, setTotalPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const language = useAppSelector((state) => state.system.language);

  /**
   * DOWNLOAD REPORT CARD
   */
  const handleDownload = (id: number, requiredLogin: boolean) => {
    if (!dataUser && requiredLogin) {
      setOpenLoginSSO(true);
      return;
    }
    window.open(`${getStaticURL('ANALYSIS_REPORT_PREVIEW', language)}/${id}?login=${requiredLogin}`, '_blank');
  };

  // Convert Report data
  const convertReportListData = useCallback(
    (repoList: ReportListDataTypes[]) => {
      if (repoList) {
        return repoList.map((item) => ({
          date: new Date(item.createdAt).toLocaleDateString(),
          title: item.stockSymbol,
          subTitle: item.type?.name,
          description: item.name,
          bntText: t('general.login'),
          background: item.style?.bgColor || '#e7fdd9',
          hasLogin: dataUser ? false : item.requiredLogin,
          handleDownload: () => handleDownload(item.id, item.requiredLogin),
          isShowDownload: !!item.file,
        }));
      }

      return [];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dataUser],
  );

  const initData = async (codeParams?: string) => {
    try {
      setLoadingReportList(true);
      let dataClone;
      let metaClone;
      if (isHomePage) {
        const { data, meta } = await getReportListDataService({
          limit: 10,
        });
        dataClone = data;
        metaClone = meta;
      } else {
        const res = await getReportCategoriesListService();
        res.reverse();
        const cateList = [
          {
            id: -1,
            name: t('dashboard.all_reports'),
            code: 'all',
          },
          ...res,
        ];
        setReportCategoryData(cateList);
        const { data, meta } = await getReportListDataService({
          limit: 15,
          page: 1,
          category_code: codeParams || undefined,
        });
        dataClone = data;
        metaClone = meta;
      }

      setCurrentPage(1);
      setTotalPage(metaClone.totalPages);
      setTotal(metaClone.total);
      const reportList = convertReportListData(dataClone);
      setReportListData(reportList);
    } finally {
      setLoadingReportList(false);
    }
  };

  useEffect(() => {
    if (ready || isChangeLanguage) {
      if (codeUrl) {
        initData(codeUrl);
      } else {
        initData();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUser, ready, isChangeLanguage]);

  return {
    reportListData,
    totalPage,
    total,
    currentPage,
    reportCategoryData,
    loadingReportList,
    openLoginSSO,
    setLoadingReportList,
    setCurrentPage,
    setTotalPage,
    setTotal,
    setReportListData,
    convertReportListData,
    setOpenLoginSSO,
  };
};

export default useDownloadReport;
